import {
  GoogleAuthProvider,
  signInWithCredential,
  type Auth,
  type UserCredential,
} from 'firebase/auth';
import { getLocalhostHostname, getLocalhostIP } from '@/config/environment-config';
import { isConnectedToEmulator, TEST_USERS } from '@/lib/auth-emulator';

const EMULATOR_QUICK_SIGN_IN_DENIED =
  'Emulator quick sign-in is only available on localhost with the Auth emulator running.';

function isLocalDevHostname(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === getLocalhostHostname() || hostname === getLocalhostIP();
}

/**
 * True only when all local emulator gates pass. Never true on production/cloud deploys.
 */
export function isEmulatorQuickSignInAllowed(auth: Auth | null | undefined): auth is Auth {
  if (!import.meta.env.DEV) return false;
  if (import.meta.env.VITE_USE_EMULATOR !== 'true') return false;
  if (!isLocalDevHostname()) return false;
  if (!auth) return false;
  return isConnectedToEmulator(auth);
}

export function assertEmulatorQuickSignInAllowed(
  auth: Auth | null | undefined,
): asserts auth is Auth {
  if (!isEmulatorQuickSignInAllowed(auth)) {
    throw new Error(EMULATOR_QUICK_SIGN_IN_DENIED);
  }
}

export type EmulatorGoogleUserKey = keyof typeof TEST_USERS;

export const EMULATOR_GOOGLE_TEST_USERS = (
  Object.entries(TEST_USERS) as Array<[EmulatorGoogleUserKey, (typeof TEST_USERS)[EmulatorGoogleUserKey]]>
).map(([key, user]) => ({
  key,
  label: key.charAt(0).toUpperCase() + key.slice(1),
  email: user.email,
  displayName: user.displayName,
}));

/**
 * Build a mock Google credential accepted by the Auth emulator.
 * @see https://firebase.google.com/docs/emulator-suite/connect_auth
 */
export function buildEmulatorGoogleCredential(email: string, displayName: string) {
  const sub = `google-${email.replace(/[@.]/g, '-')}`;
  return GoogleAuthProvider.credential(
    JSON.stringify({
      sub,
      email,
      email_verified: true,
      name: displayName,
    }),
  );
}

export async function signInWithEmulatorGoogleMock(
  auth: Auth | null | undefined,
  userKey: EmulatorGoogleUserKey,
): Promise<UserCredential> {
  assertEmulatorQuickSignInAllowed(auth);

  const user = TEST_USERS[userKey];
  if (!user) {
    throw new Error(`Unknown emulator test user: ${userKey}`);
  }

  const credential = buildEmulatorGoogleCredential(user.email, user.displayName);
  return signInWithCredential(auth, credential);
}
